import { Star, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronsUpDown, ChevronUp, CircleAlert, CircleCheck, Info, Plus, RotateCw, Search, TriangleAlert, X } from 'lucide-react';
import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { formatMoney } from '@despachador/domain';
import { ApiError, NETWORK_ERROR } from '@despachador/api-client';
import { focusables, useLayer } from './layer';

export { fromMinor, minorDigits, toMinor } from '@despachador/domain';

// ---------------------------------------------------------------- branding
export interface Branding {
  brandName: string;
  primaryColor: string;
  secondaryColor: string;
  fontFamily?: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
}

function rgbOf(hex: string): [number, number, number] | null {
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = [...h].map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG relative luminance. */
function luminance(hex: string): number {
  const rgb = rgbOf(hex) ?? [0, 0, 0];
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const contrastRatio = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
const LIGHT_SURFACE = luminance('#ffffff');
const DARK_SURFACE = luminance('#17181b');

/** Text color (white or near black) with the best WCAG contrast on top of `hex`. */
function contrastOf(hex: string): string {
  const l = luminance(hex);
  return contrastRatio(l, LIGHT_SURFACE) >= contrastRatio(l, luminance('#111111')) ? '#ffffff' : '#111111';
}

/**
 * White label: the same bundle renders any brand. Besides the raw brand color it derives
 * `--brand-on-light` / `--brand-on-dark`: the brand tinted just enough to stay readable (links,
 * focus rings, active nav) when a tenant picks a very light or very dark primary color.
 */
export function applyBranding(b: Branding, appName: string) {
  const root = document.documentElement.style;
  const brand = rgbOf(b.primaryColor) ? b.primaryColor : '#0f62fe';
  const l = luminance(brand);
  root.setProperty('--brand', brand);
  root.setProperty('--brand-contrast', contrastOf(brand));
  root.setProperty('--brand-on-light', contrastRatio(l, LIGHT_SURFACE) >= 4.5 ? brand : `color-mix(in srgb, ${brand} 55%, #000)`);
  root.setProperty('--brand-on-dark', contrastRatio(l, DARK_SURFACE) >= 4.5 ? brand : `color-mix(in srgb, ${brand} 45%, #fff)`);
  // one colour per brand (owner's decision, 2026-10-31): the dark bars of the driver app (top bar,
  // call) take the primary, darkened until white text reads on it; the stored secondary is not used
  root.setProperty('--brand-2', contrastRatio(l, luminance('#ffffff')) >= 4.5 ? brand : `color-mix(in srgb, ${brand} 55%, #000)`);
  if (b.fontFamily && b.fontFamily !== 'system-ui') root.setProperty('--font', `${b.fontFamily}, system-ui, sans-serif`);
  document.title = `${b.brandName} · ${appName}`;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', brand);
  if (b.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = b.faviconUrl;
  }
}

// ---------------------------------------------------------------- formatting
/** Locale and time zone of the tenant; set once after login so every screen formats alike. */
const fmt = { locale: 'es-CO', timeZone: undefined as string | undefined };
export function setFormatContext(locale?: string | null, timeZone?: string | null) {
  if (locale) fmt.locale = locale;
  if (timeZone) fmt.timeZone = timeZone;
}
const dtf = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(fmt.locale, { timeZone: fmt.timeZone, ...o });

export const money = (minor: number | null | undefined, currency = 'COP') => (minor === null || minor === undefined ? '—' : formatMoney(Number(minor), currency, fmt.locale));
export const dateTime = (v: string | Date | null | undefined) => (v ? dtf({ dateStyle: 'short', timeStyle: 'short' }).format(new Date(v)) : '—');
/** The month in words ("24 oct 2026, 07:58"): for lists read by people, where 4/10/26 is ambiguous. */
export const dateTimeLong = (v: string | Date | null | undefined) => {
  if (!v) return '—';
  const p = Object.fromEntries(
    dtf({ day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date(v))
      .map((x) => [x.type, x.value]),
  );
  return `${p.day} ${String(p.month).replace(/[.]/g, '')} ${p.year}, ${p.hour}:${p.minute}`;
};
export const timeOnly =(v: string | Date | null | undefined) => (v ? dtf({ timeStyle: 'short' }).format(new Date(v)) : '—');
/** Calendar dates (YYYY-MM-DD, e.g. expiry or payment day) have no time zone: never shift them. */
export const dateOnly = (v: string | null | undefined) => {
  if (!v) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (!m) return v;
  return new Intl.DateTimeFormat(fmt.locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};
export const km = (m: number | null | undefined) => (m === null || m === undefined ? '—' : `${(m / 1000).toFixed(1)} km`);
export const minutes = (s: number | null | undefined) => (s === null || s === undefined ? '—' : `${Math.max(1, Math.round(s / 60))} min`);
/** Local date as YYYY-MM-DD (toISOString() would jump to tomorrow in the evening in the Americas). */
export const todayInput = (offsetDays = 0) => {
  const d = new Date(Date.now() + offsetDays * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ---------------------------------------------------------------- domain labels
export type Tone = '' | 'ok' | 'warn' | 'danger' | 'info';
export type StatusMap = Record<string, { label: string; tone: Tone }>;

/** Human label for a code: never show raw enum values when a translation exists. */
export const label = (map: Record<string, string | { label: string }>, code: string | null | undefined) => {
  if (code === null || code === undefined || code === '') return '—';
  const v = map[code];
  return v === undefined ? code : typeof v === 'string' ? v : v.label;
};

export const SERVICE_STATUS: StatusMap = {
  CREATED: { label: 'Creado', tone: '' },
  COLLECTING_DATA: { label: 'Recolectando datos', tone: '' },
  READY_FOR_DISPATCH: { label: 'Por despachar', tone: 'warn' },
  DISPATCHING: { label: 'Buscando conductor', tone: 'info' },
  ACCEPTED: { label: 'Aceptado', tone: 'info' },
  DRIVER_EN_ROUTE: { label: 'En camino', tone: 'info' },
  DRIVER_ARRIVED: { label: 'En el punto', tone: 'info' },
  IN_PROGRESS: { label: 'En viaje', tone: 'info' },
  COMPLETED: { label: 'Completado', tone: 'ok' },
  CLOSED: { label: 'Cerrado', tone: 'ok' },
  CANCELLED_BY_CLIENT: { label: 'Cancelado (cliente)', tone: 'danger' },
  CANCELLED_BY_DRIVER: { label: 'Cancelado (conductor)', tone: 'danger' },
  CANCELLED_BY_ADMIN: { label: 'Cancelado (operación)', tone: 'danger' },
  NO_SHOW: { label: 'No se presentó', tone: 'danger' },
  REJECTED: { label: 'Rechazado', tone: 'danger' },
  EXPIRED: { label: 'Sin conductor', tone: 'danger' },
  DISPUTED: { label: 'En disputa', tone: 'warn' },
};

export const PRESENCE: StatusMap = {
  ONLINE: { label: 'Disponible', tone: 'ok' },
  BUSY: { label: 'Ocupado', tone: 'info' },
  PAUSED: { label: 'En pausa', tone: 'warn' },
  OFFLINE: { label: 'Desconectado', tone: '' },
  SUSPENDED: { label: 'Suspendido', tone: 'danger' },
};

export const ACCESS: StatusMap = {
  ACTIVE: { label: 'Al día', tone: 'ok' },
  GRACE_PERIOD: { label: 'Periodo de gracia', tone: 'warn' },
  OVERDUE: { label: 'Vencida', tone: 'danger' },
  SUSPENDED: { label: 'Suspendida', tone: 'danger' },
  CANCELLED: { label: 'Cancelada', tone: '' },
  NONE: { label: 'Sin plan', tone: '' },
};

export const OFFER_STATUS: StatusMap = {
  OFFERED: { label: 'Enviada', tone: 'info' },
  VIEWED: { label: 'Vista', tone: 'info' },
  ACCEPTED: { label: 'Aceptada', tone: 'ok' },
  REJECTED: { label: 'Rechazada', tone: 'danger' },
  EXPIRED: { label: 'Sin respuesta', tone: '' },
  CANCELLED: { label: 'Retirada', tone: '' },
};

export const DOCUMENT_STATUS: StatusMap = {
  APPROVED: { label: 'Aprobado', tone: 'ok' },
  PENDING_REVIEW: { label: 'En revisión', tone: 'warn' },
  REJECTED: { label: 'Rechazado', tone: 'danger' },
};

/** One state per document type (`requirements[]` from the API). */
export const REQUIREMENT_STATE: StatusMap = {
  VALID: { label: 'Vigente', tone: 'ok' },
  EXPIRING: { label: 'Por vencer', tone: 'warn' },
  PENDING_REVIEW: { label: 'En revisión', tone: 'info' },
  REJECTED: { label: 'Rechazado', tone: 'danger' },
  EXPIRED: { label: 'Vencido', tone: 'danger' },
  MISSING: { label: 'Falta', tone: 'danger' },
};

export type DocumentRequirement = {
  documentTypeId: string;
  documentTypeName: string;
  required: boolean;
  requiresExpiry: boolean;
  state: 'MISSING' | 'PENDING_REVIEW' | 'REJECTED' | 'VALID' | 'EXPIRING' | 'EXPIRED';
  documentId: string | null;
  expiresAt: string | null;
  pendingDocumentId: string | null;
  reviewNotes: string | null;
};

/** The line under a requirement's name: what the state means for that document. */
export function requirementDetail(r: DocumentRequirement): string {
  const optional = r.required ? '' : ' · opcional';
  switch (r.state) {
    case 'VALID':
    case 'EXPIRING':
      return `${r.expiresAt ? `Vence el ${dateOnly(r.expiresAt)}` : 'Sin vencimiento'}${r.pendingDocumentId ? ' · renovación en revisión' : ''}${optional}`;
    case 'EXPIRED':
      return `Venció el ${dateOnly(r.expiresAt)}${optional}`;
    case 'PENDING_REVIEW':
      return `Enviado, esperando revisión${optional}`;
    case 'REJECTED':
      return `${r.reviewNotes ? `Rechazado: ${r.reviewNotes}` : 'Rechazado sin motivo indicado'}${optional}`;
    default:
      return r.required ? 'Obligatorio, sin registrar' : 'Opcional, sin registrar';
  }
}

/** States that ask the owner to send something new. */
export const requirementNeedsAction = (r: DocumentRequirement) => r.state === 'MISSING' || r.state === 'REJECTED' || r.state === 'EXPIRED' || (r.state === 'EXPIRING' && !r.pendingDocumentId);

export const VEHICLE_STATUS: StatusMap = {
  ACTIVE: { label: 'Activo', tone: 'ok' },
  INACTIVE: { label: 'Inactivo', tone: '' },
  MAINTENANCE: { label: 'En mantenimiento', tone: 'warn' },
  BLOCKED: { label: 'Bloqueado', tone: 'danger' },
};

export const USER_STATUS: StatusMap = {
  ACTIVE: { label: 'Activo', tone: 'ok' },
  INVITED: { label: 'Invitado', tone: 'info' },
  SUSPENDED: { label: 'Suspendido', tone: 'warn' },
  DISABLED: { label: 'Desactivado', tone: 'danger' },
};

export const CUSTOMER_STATUS: StatusMap = {
  ACTIVE: { label: 'Activo', tone: 'ok' },
  BLOCKED: { label: 'Bloqueado', tone: 'danger' },
};

export const EXPORT_STATUS: StatusMap = {
  PENDING: { label: 'En cola', tone: '' },
  RUNNING: { label: 'Generando', tone: 'info' },
  DONE: { label: 'Lista', tone: 'ok' },
  FAILED: { label: 'Falló', tone: 'danger' },
};

export const CONVERSATION_STATUS: StatusMap = {
  OPEN: { label: 'Abierta', tone: 'info' },
  CLOSED: { label: 'Cerrada', tone: '' },
};

/** Steps of the WhatsApp flow (CONVERSATION_STATES in the API). */
export const CONVERSATION_STEP: Record<string, string> = {
  REQUEST_SERVICE: 'Menú inicial',
  ASK_ORIGIN: 'Pide origen',
  ASK_DESTINATION: 'Pide destino',
  ASK_VEHICLE: 'Elige vehículo',
  SHOW_QUOTE: 'Cotización',
  ASK_TIME: 'Pide fecha y hora',
  ASK_NAME: 'Pide nombre',
  ASK_BIRTHDATE: 'Pide cumpleaños',
  ASK_EMAIL: 'Pide correo',
  CONFIRM: 'Confirmando',
  DISPATCH: 'Buscando conductor',
  TRACKING: 'En servicio',
  RATE: 'Calificación',
  CLOSED: 'Finalizada',
};

export const CHANNEL: Record<string, string> = { WHATSAPP: 'WhatsApp', ADMIN: 'Central', WEB: 'Web', APP: 'App' };
export const PLAN_KIND: Record<string, string> = { MONTHLY: 'Mensual', WEEKLY: 'Semanal', DAILY: 'Diario', PER_SERVICE: 'Por servicio', HYBRID: 'Híbrido', EXEMPT: 'Exento (sin cuota)' };
export const ZONE_KIND: Record<string, string> = { ZONE: 'Zona', AIRPORT: 'Aeropuerto', CITY: 'Ciudad' };
export const ACTOR_TYPE: Record<string, string> = { USER: 'Operador', DRIVER: 'Conductor', CUSTOMER: 'Cliente', SYSTEM: 'Sistema', PLATFORM: 'Plataforma' };
export const DISPATCH_STRATEGY: Record<string, string> = {
  NEAREST: 'Más cercano',
  ROUND_ROBIN: 'Rotación equitativa',
  LEAST_ASSIGNED: 'Menos servicios asignados',
  ZONE: 'Por zona',
  PRIORITY: 'Por prioridad del conductor',
  SCORE: 'Puntaje combinado',
};
export const LOYALTY_TX: Record<string, string> = {
  EARN: 'Acumulación',
  REDEEM: 'Canje',
  ADJUST: 'Ajuste manual',
  EXPIRE: 'Vencimiento',
  BONUS: 'Bono',
  REFERRAL: 'Referido',
  BIRTHDAY: 'Cumpleaños',
  FREQUENCY: 'Bono de frecuencia',
  REVERSAL: 'Reversión',
};
export const SERVICE_EVENT: Record<string, string> = {
  created: 'Servicio creado',
  'dispatch.stage_started': 'Inicia etapa de búsqueda',
  'dispatch.stage_timeout': 'Etapa sin respuesta',
  'dispatch.stage_waiting': 'Esperando conductores disponibles',
  'dispatch.human_stage': 'Pasa a despacho manual',
  driver_dropped: 'El conductor liberó el servicio',
};

export const ELIGIBILITY_REASON: Record<string, string> = {
  DRIVER_NOT_ACTIVE: 'Conductor inactivo',
  DRIVER_SUSPENDED: 'Conductor suspendido',
  USER_DISABLED: 'Usuario deshabilitado',
  NO_PERMISSION: 'Sin permiso para recibir ofertas',
  NOT_CONNECTED: 'Desconectado',
  NOT_AVAILABLE: 'No disponible (pausa/ocupado)',
  HAS_ACTIVE_SERVICE: 'Tiene un servicio activo',
  HAS_PENDING_OFFER: 'Tiene otra oferta pendiente',
  ACCESS_BLOCKED: 'Cuota no vigente',
  DRIVER_DOCUMENTS_INVALID: 'Documentos del conductor inválidos',
  NO_VEHICLE: 'Sin vehículo',
  VEHICLE_NOT_ACTIVE: 'Vehículo inactivo',
  VEHICLE_DOCUMENTS_INVALID: 'Documentos del vehículo inválidos',
  VEHICLE_INCOMPATIBLE: 'Tipo de vehículo incompatible',
  VEHICLE_CAPACITY: 'Capacidad insuficiente',
  NO_LOCATION: 'Sin ubicación reciente',
  OUTSIDE_ZONE: 'Fuera de su zona',
  OUTSIDE_RADIUS: 'Fuera del radio',
  PREVIOUSLY_OFFERED: 'Ya recibió la oferta',
};

// ---------------------------------------------------------------- errors
/** API field names → the words the person saw on the form. */
const FIELD_LABELS: Record<string, string> = {
  fullName: 'Nombre',
  name: 'Nombre',
  email: 'Correo',
  phone: 'Teléfono',
  customerPhone: 'Teléfono del cliente',
  customerName: 'Nombre del cliente',
  password: 'Contraseña',
  plate: 'Placa',
  make: 'Marca',
  model: 'Modelo',
  year: 'Año',
  color: 'Color',
  capacity: 'Capacidad',
  key: 'Clave',
  amountMinor: 'Monto',
  priceMinor: 'Precio',
  reason: 'Motivo',
  points: 'Puntos',
  hostname: 'Dominio',
  primaryColor: 'Color primario',
  secondaryColor: 'Color secundario',
  logoUrl: 'Logo',
  faviconUrl: 'Favicon',
  supportEmail: 'Correo de soporte',
  supportPhone: 'Teléfono de soporte',
  timezone: 'Zona horaria',
  startsAt: 'Inicio',
  endsAt: 'Fin',
  day: 'Fecha',
  paidOn: 'Fecha del pago',
  periods: 'Periodos',
  licenseNumber: 'Licencia',
  expiresAt: 'Vencimiento',
  number: 'Número',
  vehicleTypeId: 'Tipo de vehículo',
  origin: 'Origen',
  destination: 'Destino',
  notes: 'Notas',
  kind: 'Tipo',
  performedAt: 'Fecha',
  nextDueAt: 'Próximo mantenimiento',
  costMinor: 'Costo',
  odometerKm: 'Kilometraje',
  polygon: 'Zona en el mapa',
  feeMinor: 'Recargo',
  costPoints: 'Costo en puntos',
  value: 'Valor',
  multiplierPct: 'Multiplicador',
  bonusPoints: 'Puntos de bono',
  stages: 'Etapa',
  timeoutS: 'Espera',
  batchSize: 'Conductores por etapa',
  radiusKm: 'Radio',
  defaultRadiusKm: 'Radio por defecto',
  maxRounds: 'Rondas máximas',
  roleIds: 'Roles',
  permissions: 'Permisos',
  body: 'Texto',
  priority: 'Prioridad',
  includedServices: 'Servicios incluidos',
  baseFareMinor: 'Tarifa base',
  perKmMinor: 'Valor por km',
  perMinuteMinor: 'Valor por minuto',
  minimumFareMinor: 'Tarifa mínima',
  roundingMinor: 'Redondeo',
  phoneNumberId: 'Phone number ID',
  displayPhone: 'Número visible',
  appSecret: 'App secret',
};

function fieldLabel(path: string): string {
  const parts = path.split('.').filter(Boolean);
  const named = parts.filter((p) => !/^\d+$/.test(p));
  const index = parts.find((p) => /^\d+$/.test(p));
  const last = named.at(-1) ?? '';
  const text = FIELD_LABELS[last] ?? last;
  return index !== undefined ? `${text} (fila ${Number(index) + 1})` : text;
}

/**
 * One sentence a person can act on: what happened and what to do. Never raw stack traces,
 * English framework messages or internal field names.
 */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === NETWORK_ERROR || err.status === 0) return 'No hay conexión con el servidor. Revisa tu conexión a internet e inténtalo de nuevo.';
    if (err.status >= 500)
      return `Ocurrió un error inesperado en el servidor. Inténtalo de nuevo; si persiste, comparte con soporte el código ${err.requestId ?? 'de la hora del error'}.`;
    if (err.status === 429) return 'Demasiadas solicitudes seguidas. Espera unos segundos e inténtalo de nuevo.';
    if (err.status === 403 && err.message === 'No autorizado') return 'No tienes permiso para realizar esta acción. Si lo necesitas, pídelo a un administrador.';
    if (Array.isArray(err.details) && err.details.length) {
      const lines = err.details
        .filter((d: any) => d && typeof d.message === 'string')
        .map((d: any) => (d.path ? `${fieldLabel(String(d.path))}: ${d.message}` : d.message));
      if (lines.length) return `Revisa los datos. ${lines.join(' · ')}`;
    }
    return err.message;
  }
  const message = (err as Error)?.message;
  return message && !/^(TypeError|Failed to fetch|NetworkError)/.test(message) ? message : 'Ocurrió un error inesperado. Inténtalo de nuevo.';
}

// ---------------------------------------------------------------- errors next to the field
/**
 * Validation errors of the last action, by API field name. A `Field` that declares its `name`
 * shows its own error under the control; what no field claims still goes to the toast.
 * One store for the app is enough: only one action runs at a time (see useAction).
 */
const fieldErrors = {
  current: {} as Record<string, string>,
  mounted: new Map<string, number>(),
  listeners: new Set<() => void>(),
  set(next: Record<string, string>) {
    if (!Object.keys(next).length && !Object.keys(this.current).length) return;
    this.current = next;
    this.listeners.forEach((l) => l());
  },
};
const subscribeFieldErrors = (l: () => void) => {
  fieldErrors.listeners.add(l);
  return () => void fieldErrors.listeners.delete(l);
};

/** Hands each validation problem to the field that owns it. True when every problem found its field. */
function claimFieldErrors(err: unknown): boolean {
  if (!(err instanceof ApiError) || !Array.isArray(err.details) || !err.details.length) return false;
  const next: Record<string, string> = {};
  let all = true;
  for (const d of err.details as Array<{ path?: unknown; message?: unknown }>) {
    const name = String(d?.path ?? '')
      .split('.')
      .filter((p) => p && !/^\d+$/.test(p))
      .at(-1);
    if (name && typeof d.message === 'string' && fieldErrors.mounted.has(name)) next[name] ??= d.message;
    else all = false;
  }
  fieldErrors.set(next);
  if (Object.keys(next).length) setTimeout(() => document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
  return all;
}

function useFieldError(name: string | undefined): string | undefined {
  useEffect(() => {
    if (!name) return;
    fieldErrors.mounted.set(name, (fieldErrors.mounted.get(name) ?? 0) + 1);
    return () => {
      const n = (fieldErrors.mounted.get(name) ?? 1) - 1;
      if (n > 0) fieldErrors.mounted.set(name, n);
      else fieldErrors.mounted.delete(name);
    };
  }, [name]);
  const all = useSyncExternalStore(subscribeFieldErrors, () => fieldErrors.current);
  return name ? all[name] : undefined;
}

// ---------------------------------------------------------------- small components
export function Badge({ tone = '', children }: { tone?: string; children: ReactNode }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function StatusBadge({ status, map = SERVICE_STATUS }: { status: string; map?: Record<string, { label: string; tone: string }> }) {
  const s = map[status] ?? { label: status, tone: '' };
  return (
    <Badge tone={s.tone}>
      <span className="dot" aria-hidden /> {s.label}
    </Badge>
  );
}

export function Spinner({ label = 'Cargando', block }: { label?: string; block?: boolean }) {
  return (
    <span role="status" className={block ? 'loading-block' : 'row'}>
      <span className="spinner" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** Loading placeholder with the shape of a list: what is coming, not a spinner in empty space. */
export function Skeleton({ rows = 5, label = 'Cargando' }: { rows?: number; label?: string }) {
  return (
    <div className="skeleton" role="status">
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} aria-hidden />
      ))}
      <span className="sr-only">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------- service lifecycle
/**
 * The six stages a trip goes through. The rail is the product's signature: the same marks in
 * lists, the record panel and the map, so the state of a trip is read without reading words.
 */
export const SERVICE_STAGES = ['Solicitado', 'Asignado', 'En camino', 'En el punto', 'En viaje', 'Finalizado'] as const;
const STAGE_OF: Record<string, number> = {
  CREATED: 0,
  COLLECTING_DATA: 0,
  READY_FOR_DISPATCH: 0,
  DISPATCHING: 0,
  ACCEPTED: 1,
  DRIVER_EN_ROUTE: 2,
  DRIVER_ARRIVED: 3,
  IN_PROGRESS: 4,
  DISPUTED: 4,
  COMPLETED: 5,
  CLOSED: 5,
};
/** Stage index of a status (0…5), or -1 when the trip ended without completing (cancelled, expired…). */
export const serviceStage = (status: string) => STAGE_OF[status] ?? -1;

export function StateRail({ status, label: own }: { status: string; label?: string }) {
  const stage = serviceStage(status);
  const stopped = stage < 0;
  const name = own ?? `${label(SERVICE_STATUS, status)}${stopped ? '' : ` · etapa ${stage + 1} de ${SERVICE_STAGES.length}`}`;
  // the current mark takes the tone of the state: waiting (warn), moving (info), done (ok), stopped (danger)
  const tone = stopped ? 'danger' : stage === SERVICE_STAGES.length - 1 ? 'ok' : stage === 0 ? 'warn' : 'info';
  return (
    // an empty label: decorative, the word next to it already says the state
    <span className={`rail t-${tone}`} {...(own === '' ? { 'aria-hidden': true } : { role: 'img', 'aria-label': name, title: name })}>
      {SERVICE_STAGES.map((s, i) => (
        <span key={s} className={i < stage ? 'done' : i === stage ? 'now' : undefined} />
      ))}
    </span>
  );
}

/**
 * The state of a trip, everywhere a trip is listed: its rail and the word. (Other records use
 * StatusBadge.) Waiting and problem states also color the word; moving and done ones do not.
 */
export function ServiceStatus({ status, rail = true, scheduled }: { status: string; rail?: boolean; scheduled?: boolean }) {
  // a scheduled trip before its dispatch window is not waiting yet: no warning tone
  const s = scheduled && status === 'READY_FOR_DISPATCH' ? { label: 'Programado', tone: '' } : (SERVICE_STATUS[status] ?? { label: status, tone: '' });
  return (
    <span className={`svc-status${s.tone === 'warn' || s.tone === 'danger' ? ` ${s.tone}` : ''}`}>
      {rail && <StateRail status={status} label="" />}
      <span>{s.label}</span>
    </span>
  );
}

/** The rail with the name of each stage under its mark: the head of a trip record. */
export function StageTrack({ status }: { status: string }) {
  const stage = serviceStage(status);
  const stopped = stage < 0;
  return (
    <ol className={`stages${stopped ? ' stopped' : ''}`} aria-label={`Etapas del servicio: ${label(SERVICE_STATUS, status)}`}>
      {SERVICE_STAGES.map((s, i) => {
        const state = stopped ? '' : i < stage ? 'done' : i === stage ? (stage === SERVICE_STAGES.length - 1 ? 'done now' : stage === 0 ? 'now wait' : 'now') : '';
        return (
          <li key={s} className={state || undefined} aria-current={i === stage ? 'step' : undefined}>
            {s}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Time since `from` as the dispatch board shows it ("4′", "1 h 12′"). `warn` and `danger` are the
 * minutes after which it changes tone; the word in `title` keeps it readable without the color.
 */
/** A wait in the one short format used everywhere (queue, map, record): 7′ · 1h05 · 3 d (UX-012). */
export const ageText = (min: number) => (min < 60 ? `${min}′` : min < 1440 ? `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}` : `${Math.floor(min / 1440)} d`);

export function Age({ from, now, warn = 5, danger = 10, label: what = 'Esperando' }: { from: string | Date | null | undefined; now: number; warn?: number; danger?: number; label?: string }) {
  if (!from) return <span className="age">—</span>;
  const min = Math.max(0, Math.floor((now - new Date(from).getTime()) / 60_000));
  // short on purpose (it lives in a narrow column)
  const text = ageText(min);
  const tone = min >= danger ? 'danger' : min >= warn ? 'warn' : '';
  return (
    <span className={`age ${tone}`} title={`${what} desde hace ${min} min`}>
      {text}
      <span className="sr-only"> ({what.toLowerCase()} desde hace {min} minutos)</span>
    </span>
  );
}

/** Empty state: what is going on and, when possible, what to do next. */
export function Empty({ children, title, action }: { children?: ReactNode; title?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      {title && <div className="empty-title">{title}</div>}
      {children && <div>{children}</div>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-state" role="alert">
      <CircleAlert size={20} aria-hidden />
      <div className="stack" style={{ gap: 6 }}>
        <span>{message}</span>
        {onRetry && (
          <span>
            <button type="button" className="btn sm" onClick={onRetry}>
              <RotateCw size={14} aria-hidden /> Reintentar
            </button>
          </span>
        )}
      </div>
    </div>
  );
}

const NOTICE_ICON = { info: Info, ok: CircleCheck, warn: TriangleAlert, danger: CircleAlert };

/**
 * Inline message about the state of things: what happens and, when possible, what to do about it.
 * The icon carries the tone together with the color. `urgent` announces it to screen readers at once.
 */
export function Notice({ tone = 'info', title, children, action, urgent }: { tone?: 'info' | 'ok' | 'warn' | 'danger'; title?: ReactNode; children?: ReactNode; action?: ReactNode; urgent?: boolean }) {
  const Icon = NOTICE_ICON[tone];
  return (
    <div className={`notice ${tone}`} role={urgent ? 'alert' : undefined}>
      <Icon size={18} aria-hidden className="notice-icon" />
      <div className="notice-text">
        {title && <b>{title}</b>}
        {children && <div className="small">{children}</div>}
      </div>
      {action && <div className="notice-action">{action}</div>}
    </div>
  );
}

interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void> | void;
}

/** Loading → error (with retry) → content. Replaces bare `if (!data) return <Spinner />`, which spun forever on errors. */
export function Loadable<T>({ state, children }: { state: AsyncState<T>; children: (data: T) => ReactNode }) {
  if (state.data !== null && state.data !== undefined) return <>{children(state.data)}</>;
  if (state.error) return <ErrorState message={state.error} onRetry={() => void state.reload()} />;
  return <Skeleton rows={4} />;
}

/**
 * The one way to show a figure: what it is, the value and its context (period, comparison, what
 * it implies). `info` is an optional definition next to the label; children go under the value
 * (a comparison against another period); `compact` is for narrow tiles on phones.
 */
export function Stat({ label, value, hint, info, compact, children }: { label: string; value: ReactNode; hint?: ReactNode; info?: ReactNode; compact?: boolean; children?: ReactNode }) {
  return (
    <div className={`card stat-tile${compact ? ' compact' : ''}`}>
      <div className="stat-label">
        <span>{label}</span>
        {info}
      </div>
      <div className="stat-value">{value}</div>
      {children}
      {hint && <div className="muted small">{hint}</div>}
    </div>
  );
}

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {description && <p className="muted small">{description}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

const CONTROL_TAGS = new Set(['input', 'select', 'textarea']);

/**
 * Visible label + optional hint/error, wired with ids so screen readers announce all of it.
 * A single input/select/textarea child gets `id` and `aria-describedby`; any other child
 * (a group of checkboxes, two inputs…) is exposed as a labelled group.
 * `name` is the API field: a validation error the server reports for it shows here until the value changes.
 */
export function Field({
  label,
  children,
  hint,
  error: ownError,
  optional,
  name,
  start,
  end,
  under,
}: {
  label: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  name?: string;
  /** A decorative icon inside the control, at its start (only with a single control). */
  start?: ReactNode;
  /** A button inside the control, at its end (e.g. show the password; only with a single control). */
  end?: ReactNode;
  /** A drawing under a transparent control (e.g. the cells of a code); hidden from assistive tech. */
  under?: ReactNode;
}) {
  const id = useId();
  const serverError = useFieldError(name);
  const error = ownError || serverError;
  const onEdit = serverError
    ? () => {
        const { [name!]: _fixed, ...rest } = fieldErrors.current;
        fieldErrors.set(rest);
      }
    : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const text = (
    <>
      {label}
      {optional && <span className="optional"> (opcional)</span>}
    </>
  );
  const extra = (
    <>
      {hint && (
        <span id={hintId} className="field-hint">
          {hint}
        </span>
      )}
      {error && (
        <span id={errorId} className="field-error">
          {error}
        </span>
      )}
    </>
  );
  if (isValidElement(children) && typeof children.type === 'string' && CONTROL_TAGS.has(children.type)) {
    const child = children as ReactElement<Record<string, unknown>>;
    const controlId = (child.props.id as string | undefined) ?? id;
    const control = cloneElement(child, { id: controlId, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined });
    return (
      <div className="field" onChangeCapture={onEdit}>
        <label className="field-label" htmlFor={controlId}>
          {text}
        </label>
        {start || end || under ? (
          <div className={`input-affix ${start ? 'has-start' : ''} ${end ? 'has-end' : ''} ${under ? 'has-under' : ''}`}>
            {under && (
              <span className="input-affix-under" aria-hidden>
                {under}
              </span>
            )}
            {start && (
              <span className="input-affix-start" aria-hidden>
                {start}
              </span>
            )}
            {control}
            {end && <span className="input-affix-end">{end}</span>}
          </div>
        ) : (
          control
        )}
        {extra}
      </div>
    );
  }
  return (
    <div className="field" role="group" aria-labelledby={`${id}-label`} aria-describedby={describedBy} onChangeCapture={onEdit}>
      <span className="field-label" id={`${id}-label`}>
        {text}
      </span>
      {children}
      {extra}
    </div>
  );
}

/**
 * One setting per row: what it is (and what it changes) on the left, a compact control with its
 * unit on the right, both centered on the same line. For screens made of short values, where a
 * grid of full-width inputs leaves ragged gaps. Same labelling and server errors as `Field`.
 */
export function SettingRow({
  label,
  hint,
  unit,
  name,
  was,
  children,
}: {
  label: string;
  hint?: ReactNode;
  unit?: string;
  name?: string;
  /** The saved value while this one is changed and not saved yet: the row is tinted and says it. */
  was?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  const error = useFieldError(name);
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const single = isValidElement(children) && typeof children.type === 'string' && CONTROL_TAGS.has(children.type);
  const onEdit = error
    ? () => {
        const { [name!]: _fixed, ...rest } = fieldErrors.current;
        fieldErrors.set(rest);
      }
    : undefined;
  return (
    <div className={`setting${was !== undefined ? ' dirty' : ''}`} role={single ? undefined : 'group'} aria-labelledby={single ? undefined : `${id}-label`} onChangeCapture={onEdit}>
      <div className="setting-text">
        {single ? (
          <label className="setting-label" htmlFor={id}>
            {label}
          </label>
        ) : (
          <span className="setting-label" id={`${id}-label`}>
            {label}
          </span>
        )}
        {hint && (
          <span id={hintId} className="field-hint">
            {hint}
          </span>
        )}
        {error && (
          <span id={errorId} className="field-error">
            {error}
          </span>
        )}
        {was !== undefined && <span className="setting-was">Antes: {was}</span>}
      </div>
      <div className="setting-control">
        {single ? cloneElement(children as ReactElement<Record<string, unknown>>, { id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined }) : children}
        {unit && <span className="setting-unit">{unit}</span>}
      </div>
    </div>
  );
}

/** Search box with a real (visually hidden) label, debounce, Escape/clear button. */
export function SearchInput({
  value,
  onChange,
  label,
  placeholder,
  delay = 300,
  style,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  placeholder?: string;
  delay?: number;
  style?: CSSProperties;
}) {
  const [text, setText] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const debounced = useDebounced(text, delay);
  useEffect(() => {
    if (debounced !== value) onChangeRef.current(debounced);
    // only the debounced text drives the callback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  const clear = () => {
    setText('');
    onChangeRef.current('');
  };
  return (
    <div className="search" style={style}>
      <Search size={16} aria-hidden className="search-icon" />
      <input
        className="input"
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && text && (e.stopPropagation(), clear())}
      />
      {text && (
        <button type="button" className="search-clear" aria-label="Limpiar búsqueda" onClick={clear}>
          <X size={14} aria-hidden />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- overlays

/**
 * Accessible modal dialog: focus moves in (first field, or an element marked data-autofocus),
 * Tab is trapped, Escape/overlay click close it, focus returns to the trigger, page scroll locks.
 */
export function Modal({
  title,
  onClose,
  children,
  wide,
  xwide,
  confirm,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  xwide?: boolean;
  /** A short question with its answers: narrow, and on phones the answers stack full width (main first). */
  confirm?: boolean;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useLayer(ref, onClose, {
    trap: true,
    initialFocus: () => {
      const el = ref.current!;
      return el.querySelector<HTMLElement>('[data-autofocus]') ?? focusables(el).find((x) => !x.classList.contains('modal-close')) ?? el;
    },
  });
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={`modal ${xwide ? 'xwide' : wide ? 'wide' : confirm ? 'confirm' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="btn ghost icon modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Side panel for record details. Non-modal on purpose: dispatchers keep clicking other rows
 * while it is open. Escape closes it (unless a dialog is on top) and focus goes back to the row.
 * `footer` holds the actions (always visible, the body scrolls); `docked` makes it part of the
 * page layout instead of floating over it (the live board keeps its queue in view).
 */
export function Drawer({ title, label, onClose, children, footer, docked, aside }: { title: ReactNode; label: string; onClose: () => void; children: ReactNode; footer?: ReactNode; docked?: boolean; aside?: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  useLayer(ref, onClose, { trap: false, initialFocus: () => headingRef.current });
  return (
    <aside ref={ref} className={`drawer${docked ? ' docked' : ''}`} aria-label={label}>
      <div className="drawer-head">
        <h2 ref={headingRef} tabIndex={-1}>
          {title}
        </h2>
        <span className="row" style={{ flexWrap: 'nowrap' }}>
          {aside}
          <button type="button" className="btn ghost icon" onClick={onClose} aria-label="Cerrar panel">
            <X size={18} aria-hidden />
          </button>
        </span>
      </div>
      <div className="drawer-body">{children}</div>
      {footer && <div className="drawer-foot">{footer}</div>}
    </aside>
  );
}

/**
 * The one way to create a record: a button that opens the form in a dialog (a bottom sheet on
 * phones). The form receives `close` and calls it after a successful save.
 */
export function FormDialog({ label, title, primary, wide, children }: { label: string; title?: string; primary?: boolean; wide?: boolean; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="form-actions">
      <button type="button" className={`btn ${primary ? 'primary' : ''}`} onClick={() => setOpen(true)}>
        <Plus size={16} aria-hidden /> {label}
      </button>
      {open && (
        <Modal title={title ?? label} onClose={() => setOpen(false)} wide={wide}>
          {children(() => setOpen(false))}
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- confirm dialog
export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive: red button and the initial focus stays on "cancel". */
  danger?: boolean;
  /** Ask for a free-text reason (audited actions). */
  reason?: { label: string; minLength?: number; maxLength?: number; optional?: boolean; hint?: string };
  /** Ask to pick one option (required), shown before the reason; returned as `choice`. */
  choice?: { label: string; options: Array<[string, string]>; hint?: string };
}
type ConfirmResult = { reason: string; choice?: string };
type ConfirmFn = (o: ConfirmOptions) => Promise<ConfirmResult | null>;
const ConfirmCtx = createContext<ConfirmFn>(async () => null);

/** `const confirm = useConfirm(); const ok = await confirm({...}); if (!ok) return;` */
export const useConfirm = () => useContext(ConfirmCtx);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<(ConfirmOptions & { resolve: (v: ConfirmResult | null) => void }) | null>(null);
  const confirm = useCallback<ConfirmFn>((o) => new Promise((resolve) => setPending({ ...o, resolve })), []);
  const close = (v: ConfirmResult | null) => {
    pending?.resolve(v);
    setPending(null);
  };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {pending && <ConfirmDialog o={pending} onDone={close} />}
    </ConfirmCtx.Provider>
  );
}

function ConfirmDialog({ o, onDone }: { o: ConfirmOptions; onDone: (v: ConfirmResult | null) => void }) {
  const [reason, setReason] = useState('');
  const [choice, setChoice] = useState('');
  const min = o.reason && !o.reason.optional ? (o.reason.minLength ?? 3) : 0;
  const tooShort = (!!o.reason && reason.trim().length < min) || (!!o.choice && !choice);
  const submit = () => !tooShort && onDone({ reason: reason.trim(), ...(o.choice ? { choice } : {}) });
  return (
    <Modal
      title={o.title}
      onClose={() => onDone(null)}
      confirm
      footer={
        <>
          <button type="button" className="btn" onClick={() => onDone(null)} data-autofocus={o.danger && !o.reason ? '' : undefined}>
            {o.cancelLabel ?? 'Cancelar'}
          </button>
          <button type="button" className={`btn ${o.danger ? 'danger' : 'primary'}`} disabled={tooShort} onClick={submit}>
            {o.confirmLabel ?? 'Confirmar'}
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {o.message && <div>{o.message}</div>}
        {o.choice && (
          <Field label={o.choice.label} hint={o.choice.hint}>
            <select className="input" data-autofocus="" value={choice} onChange={(e) => setChoice(e.target.value)}>
              <option value="" disabled>
                Elige una opción
              </option>
              {o.choice.options.map(([v, text]) => (
                <option key={v} value={v}>
                  {text}
                </option>
              ))}
            </select>
          </Field>
        )}
        {o.reason && (
          <Field
            label={o.reason.label}
            optional={o.reason.optional}
            hint={o.reason.hint ?? (min ? `Mínimo ${min} caracteres. Queda registrado en la auditoría.` : undefined)}
          >
            <textarea className="input" rows={3} data-autofocus={o.choice ? undefined : ''} maxLength={o.reason.maxLength ?? 300} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- tabs & segmented control
/** WAI-ARIA tabs: one tab stop, arrows/Home/End move between tabs. */
export function Tabs<T extends string>({ tabs, value, onChange, label }: { tabs: Array<[T, string]>; value: T; onChange: (v: T) => void; label?: string }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const onKeyDown = (e: ReactKeyboardEvent, i: number) => {
    const n = tabs.length;
    const j = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
    if (j < 0) return;
    e.preventDefault();
    onChange(tabs[j]![0]);
    refs.current[j]?.focus();
  };
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map(([k, text], i) => (
        <button
          key={k}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          aria-selected={value === k}
          tabIndex={value === k ? 0 : -1}
          className={value === k ? 'active' : ''}
          onClick={() => onChange(k)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** Mutually exclusive filter (not navigation): toggle buttons with aria-pressed. */
export function SegmentedControl<T extends string>({ options, value, onChange, label }: { options: Array<[T, ReactNode]>; value: T; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map(([k, text]) => (
        <button key={k} type="button" aria-pressed={value === k} className={value === k ? 'active' : ''} onClick={() => onChange(k)}>
          {text}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- pagination
export const PAGE_SIZES = [10, 20, 30, 50];
const PAGE_SIZE_KEY = 'ui.pageSize';

/** Preferred rows per page, remembered per browser (a convenience: works without storage too). */
function storedPageSize(): number {
  try {
    const v = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZES.includes(v) ? v : PAGE_SIZES[0]!;
  } catch {
    return PAGE_SIZES[0]!;
  }
}

/**
 * Pagination bar: "Mostrando 11–20 de 35" · first/previous · editable page "de N" · next/last ·
 * rows per page. Presentational: works the same for client-side slices and for server-side
 * pages (pass the server total).
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = PAGE_SIZES,
  label = 'Paginación',
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizes?: number[];
  label?: string;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const [draft, setDraft] = useState(String(page));
  const sizeId = useId();
  useEffect(() => setDraft(String(page)), [page]);
  const go = (p: number) => onPageChange(Math.min(pages, Math.max(1, p)));
  // only a typed value is committed (Enter or leaving the field); a plain blur must never
  // re-apply an old page over a newer one (e.g. after a search reset the list to page 1)
  const edited = useRef(false);
  const commit = () => {
    if (!edited.current) return;
    edited.current = false;
    const n = Math.round(Number(draft));
    if (!Number.isFinite(n) || draft.trim() === '') return setDraft(String(page));
    go(n);
    setDraft(String(Math.min(pages, Math.max(1, n))));
  };
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const first = page <= 1;
  const last = page >= pages;
  const nav = (text: string, target: number, disabled: boolean, icon: ReactNode) => (
    <button type="button" className="pg-btn" data-tip={text} aria-label={text} disabled={disabled} onClick={() => go(target)}>
      {icon}
    </button>
  );
  return (
    <nav className="pagination" aria-label={label}>
      <span className="pg-range" aria-live="polite">
        Mostrando {from}–{to} de {total}
      </span>
      <div className="pg-controls">
        {nav('Primera página', 1, first, <ChevronsLeft size={16} aria-hidden />)}
        {nav('Página anterior', page - 1, first, <ChevronLeft size={16} aria-hidden />)}
        <input
          className="pg-input"
          type="text"
          inputMode="numeric"
          aria-label={`Página actual, de ${pages}`}
          value={draft}
          size={Math.max(1, String(pages).length)}
          onChange={(e) => {
            edited.current = true;
            setDraft(e.target.value.replace(/\D/g, ''));
          }}
          onBlur={commit}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') {
              edited.current = false;
              setDraft(String(page));
            }
            else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault();
              go(e.key === 'ArrowUp' ? page + 1 : page - 1);
            }
          }}
        />
        <span className="pg-of">de {pages}</span>
        {nav('Página siguiente', page + 1, last, <ChevronRight size={16} aria-hidden />)}
        {nav('Última página', pages, last, <ChevronsRight size={16} aria-hidden />)}
      </div>
      {onPageSizeChange && (
        <span className="pg-size">
          <label htmlFor={sizeId}>Filas por página:</label>
          <span className="pg-select">
            <select id={sizeId} value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
              {pageSizes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <ChevronDown size={14} aria-hidden />
          </span>
        </span>
      )}
    </nav>
  );
}

/**
 * Client-side paging of an already loaded list. The page is kept when rows refresh (realtime
 * updates must not throw the operator back to page 1) and only resets when `resetKey` changes
 * (a new search or filter), or clamps when the list shrinks.
 */
export function usePagination<T>(rows: T[] | null | undefined, resetKey?: unknown) {
  const [pageSize, setPageSizeState] = useState(storedPageSize);
  const [page, setPage] = useState(1);
  const [key, setKey] = useState(resetKey);
  if (key !== resetKey) {
    setKey(resetKey);
    setPage(1);
  }
  const total = rows?.length ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pages);
  const setPageSize = (size: number) => {
    // keep the first visible row on screen when the page size changes
    setPage(Math.floor(((current - 1) * pageSize) / size) + 1);
    setPageSizeState(size);
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size));
    } catch {
      /* storage unavailable: the choice lasts for this view only */
    }
  };
  const slice = rows ? rows.slice((current - 1) * pageSize, current * pageSize) : rows;
  return { page: current, pageSize, total, pages, slice, setPage, setPageSize };
}

// ---------------------------------------------------------------- table
export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  /** Header becomes a sort toggle (by `sortValue`, or the raw `row[key]`). Nulls always sort last. */
  sortable?: boolean;
  sortValue?: (row: T) => number | string | null | undefined;
  /**
   * Phones show each row as a list item: 'title' is the heading of the item (default: the first
   * column), 'hide' leaves the column for the detail panel. Other columns show as "label · value".
   */
  mobile?: 'title' | 'hide';
  /** Secondary column: the first to step aside when the table does not fit its container. */
  secondary?: boolean;
}

/** Below this width of the container a table that does not fit goes straight to the list. */
const TABLE_LIST_BELOW = 640;

/**
 * A table never scrolls sideways: it takes the widest layout that fits its container (a page, a
 * record panel, a dialog) and says which one in `data-fit` of the wrapper: `full`, `compact`
 * (secondary columns step aside), `tight` (also less air, and two-line cells may wrap) or `list`
 * (each row a block of "label · value" lines).
 */
function fitTable(wrap: HTMLElement) {
  const narrow = wrap.clientWidth < TABLE_LIST_BELOW;
  const steps = narrow ? ['full', 'list'] : ['full', 'compact', 'tight', 'list'];
  // a list with room lays the fields of a row side by side
  wrap.dataset.list = narrow ? 'lines' : 'columns';
  for (const fit of steps) {
    wrap.dataset.fit = fit;
    if (wrap.scrollWidth <= wrap.clientWidth + 1) break;
  }
}

/** Ref for a `.table-wrap`: keeps its table fitted as the rows or the width of the container change. */
export function useTableFit<E extends HTMLElement = HTMLDivElement>() {
  const [wrap, setWrap] = useState<E | null>(null);
  // every render: the rows may have changed
  useLayoutEffect(() => {
    if (wrap) fitTable(wrap);
  });
  useEffect(() => {
    if (!wrap) return;
    let width = wrap.clientWidth;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (wrap.clientWidth === width) return;
      width = wrap.clientWidth;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => fitTable(wrap));
    });
    observer.observe(wrap);
    void document.fonts?.ready.then(() => fitTable(wrap));
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [wrap]);
  return [setWrap, wrap] as const;
}

export type SortState = { key: string; dir: 'asc' | 'desc' };

function sortRows<T extends Record<string, any>>(rows: T[], columns: Column<T>[], sort: SortState | null): T[] {
  const col = sort && columns.find((c) => c.key === sort.key);
  if (!sort || !col) return rows;
  const get = col.sortValue ?? ((r: T) => r[col.key]);
  const k = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = get(a);
    const y = get(b);
    if (x === null || x === undefined) return y === null || y === undefined ? 0 : 1;
    if (y === null || y === undefined) return -1;
    return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * k;
  });
}

/**
 * Data table with its states: loading (first load), error + retry, empty and a subtle
 * "refreshing" look while newer data arrives. Clickable rows are reachable with Tab and open
 * with Enter/Space, not only with the mouse. Lists longer than the smallest page size get a
 * pagination bar (`paginate={false}` turns it off, `pageResetKey` goes back to page 1).
 */
export function Table<T extends Record<string, any>>({
  columns,
  rows,
  onRowClick,
  empty = 'Sin datos',
  label,
  loading,
  error,
  onRetry,
  selectedId,
  paginate = true,
  pageResetKey,
  defaultSort,
  mobileRow,
  rowTone,
}: {
  columns: Column<T>[];
  rows: T[] | null | undefined;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  label?: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  selectedId?: string | null;
  paginate?: boolean;
  pageResetKey?: unknown;
  /** Initial order of sortable tables (the user can change it from the headers). */
  defaultSort?: SortState;
  /**
   * A compact row for the list layout (phones) instead of «label · value» lines: two lines with
   * what identifies the record (UX-015). The table columns still serve the wider layouts.
   */
  mobileRow?: (row: T) => ReactNode;
  /** A row that needs someone now (an active panic alert): tinted with a bar, like the live strip. */
  rowTone?: (row: T) => 'danger' | undefined;
}) {
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);
  const sorted = useMemo(() => (rows ? sortRows(rows, columns, sort) : rows), [rows, columns, sort]);
  const pg = usePagination(sorted, `${String(pageResetKey ?? '')}|${sort?.key ?? ''}|${sort?.dir ?? ''}`);
  const [wrapRef, wrap] = useTableFit();
  const titleKey = (columns.find((c) => c.mobile === 'title') ?? columns[0])?.key;
  if (error && !rows?.length) return <ErrorState message={error} onRetry={onRetry} />;
  if (!rows || (loading && rows.length === 0)) return <Skeleton rows={6} label={label ? `Cargando ${label.toLowerCase()}` : undefined} />;
  if (rows.length === 0) return typeof empty === 'string' ? <Empty>{empty}</Empty> : <>{empty}</>;
  const paged = paginate && rows.length > PAGE_SIZES[0]!;
  const visible = paged ? pg.slice! : sorted!;
  const changePage = (fn: () => void) => {
    fn();
    // long pages: bring the top of the table back into view
    const top = wrap?.getBoundingClientRect().top ?? 0;
    if (top < 0) wrap?.scrollIntoView({ block: 'start' });
  };
  return (
    <>
      <div ref={wrapRef} className={`table-wrap ${loading ? 'refreshing' : ''}`} aria-busy={loading || undefined}>
        <table className={mobileRow ? 'table responsive has-mobile-row' : 'table responsive'} aria-label={label}>
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort?.key === c.key;
                return (
                  <th
                    key={c.key}
                    scope="col"
                    className={c.secondary ? 'cell-secondary' : undefined}
                    style={{ width: c.width, textAlign: c.align }}
                    aria-sort={c.sortable ? (active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                  >
                    {c.sortable ? (
                      <button
                        type="button"
                        className={`th-sort ${active ? 'active' : ''}`}
                        onClick={() => setSort(active && sort!.dir === 'desc' ? { key: c.key, dir: 'asc' } : { key: c.key, dir: 'desc' })}
                      >
                        {c.label}
                        {active ? sort!.dir === 'asc' ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden /> : <ChevronsUpDown size={14} aria-hidden />}
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => (
              <tr
                key={(r.id as string) ?? i}
                className={`${onRowClick ? 'clickable' : ''} ${selectedId && r.id === selectedId ? 'selected' : ''} ${rowTone?.(r) ? `tone-${rowTone(r)}` : ''}`}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.target !== e.currentTarget) return; // let buttons inside the row work
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(r);
                        }
                      }
                    : undefined
                }
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    style={{ textAlign: c.align }}
                    data-label={c.label}
                    className={[c.key === titleKey ? 'cell-title' : c.mobile === 'hide' ? 'cell-hide' : '', c.secondary ? 'cell-secondary' : ''].filter(Boolean).join(' ') || undefined}
                  >
                    {c.render ? c.render(r) : String(r[c.key] ?? '—')}
                  </td>
                ))}
                {mobileRow && <td className="cell-mobile">{mobileRow(r)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paged && (
        <Pagination
          label={label ? `Paginación de ${label.toLowerCase()}` : undefined}
          page={pg.page}
          pageSize={pg.pageSize}
          total={pg.total}
          onPageChange={(p) => changePage(() => pg.setPage(p))}
          onPageSizeChange={(s) => changePage(() => pg.setPageSize(s))}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------- toasts
type Toast = { id: number; text: string; error?: boolean };
const ToastCtx = createContext<(text: string, error?: boolean) => void>(() => undefined);

/** Toasts for transient feedback. Errors stay longer, are announced assertively and can be dismissed. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (text: string, error = false) => {
      const id = ++seq.current;
      setToasts((t) => (t.some((x) => x.text === text && x.error === error) ? t : [...t.slice(-3), { id, text, error }]));
      setTimeout(() => dismiss(id), error ? 8000 : 4000);
    },
    [dismiss],
  );
  const render = (list: Toast[]) =>
    list.map((t) => (
      <div key={t.id} className={`toast ${t.error ? 'error' : ''}`}>
        {t.error ? <CircleAlert size={16} aria-hidden className="toast-icon" /> : <CircleCheck size={16} aria-hidden className="toast-icon" />}
        <span style={{ flex: 1 }}>{t.text}</span>
        <button type="button" className="toast-close" aria-label="Cerrar aviso" onClick={() => dismiss(t.id)}>
          <X size={14} aria-hidden />
        </button>
      </div>
    ));
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts">
        <div role="status" aria-live="polite" className="stack" style={{ gap: 8 }}>
          {render(toasts.filter((t) => !t.error))}
        </div>
        <div role="alert" aria-live="assertive" className="stack" style={{ gap: 8 }}>
          {render(toasts.filter((t) => t.error))}
        </div>
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

/** Toasts + confirmation dialogs: wrap each app once. */
export function UiProvider({ children }: { children: ReactNode }) {
  return (
    <ToastProvider>
      <ConfirmProvider>{children}</ConfirmProvider>
    </ToastProvider>
  );
}

// ---------------------------------------------------------------- data hooks
/**
 * Loads data and reloads when `deps` change. Out-of-order responses are discarded (typing in
 * a search box no longer shows the results of an older query). When `deps` change the old data
 * is dropped, so a detail panel never shows record A while it is loading record B; lists that
 * prefer to keep showing the previous rows while refreshing pass `{ keepPrevious: true }`.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = [], opts: { keepPrevious?: boolean } = {}) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const seq = useRef(0);
  const keepRef = useRef(opts.keepPrevious);
  const reload = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    try {
      const value = await fnRef.current();
      if (mine !== seq.current) return;
      setData(value);
      setError(null);
    } catch (err) {
      if (mine === seq.current) setError(errorText(err));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);
  const first = useRef(true);
  useEffect(() => {
    if (!first.current && !keepRef.current) {
      setData(null);
      setError(null);
    }
    first.current = false;
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, error, loading, reload, setData };
}

/**
 * Runs an action, toasting success/failure. Returns the result or undefined on error.
 * Validation errors go to the fields that declare the API name (`<Field name>`).
 * A second call while one is running is ignored (no double submits from fast double clicks).
 */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const run = useCallback(
    async <T,>(fn: () => Promise<T>, ok?: string): Promise<T | undefined> => {
      if (running.current) return undefined;
      running.current = true;
      setBusy(true);
      fieldErrors.set({});
      try {
        const r = await fn();
        if (ok) toast(ok);
        return r;
      } catch (err) {
        // each field shows its own problem; the toast only repeats what no field could show
        toast(claimFieldErrors(err) ? 'Revisa los campos marcados.' : errorText(err), true);
        return undefined;
      } finally {
        running.current = false;
        setBusy(false);
      }
    },
    [toast],
  );
  return { run, busy };
}

export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export function useInterval(fn: () => void, ms: number | null) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (ms === null) return;
    const id = setInterval(() => ref.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

export function useNow(ms = 1000) {
  const [now, setNow] = useState(Date.now());
  useInterval(() => setNow(Date.now()), ms);
  return now;
}

/** Warns before closing the tab while a form has unsaved changes. */
export function useUnsavedChangesWarning(dirty: boolean) {
  useEffect(() => {
    if (!dirty) return;
    const onBefore = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, [dirty]);
}

/** Read-only 1-5 rating. `value` may be fractional (averages); the accessible label says the number. */
export function Stars({ value, size = 14, count }: { value: number | null | undefined; size?: number; count?: number }) {
  if (value === null || value === undefined) return <span className="muted">Sin calificaciones</span>;
  const rounded = Math.round(Number(value));
  return (
    <span className="stars" role="img" aria-label={`${Number(value).toFixed(1)} de 5${count !== undefined ? `, ${count} calificaciones` : ''}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} aria-hidden fill={i <= rounded ? 'currentColor' : 'none'} className={i <= rounded ? 'on' : ''} />
      ))}
      <span className="stars-value">{Number(value).toFixed(1)}</span>
      {count !== undefined && <span className="muted"> ({count})</span>}
    </span>
  );
}

// ---------------------------------------------------------------- sign in and account security
export * from './auth/index';
export * from './ptt';
export * from './document-file';
export * from './filters';
export * from './money-input';
